import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PostsTable = ({ 
  posts, 
  selectedPosts, 
  onSelectPost, 
  onSelectAll, 
  onEdit, 
  onToggleStatus, 
  onDelete,
  onPreview,
  sortConfig,
  onSort 
}) => {
  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} color="var(--color-muted-foreground)" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={16} color="var(--color-primary)" />
      : <Icon name="ChevronDown" size={16} color="var(--color-primary)" />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="hidden lg:block overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left p-4 w-12">
              <Checkbox
                checked={selectedPosts?.length === posts?.length && posts?.length > 0}
                indeterminate={selectedPosts?.length > 0 && selectedPosts?.length < posts?.length}
                onChange={(e) => onSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="text-left p-4 w-16"></th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('title')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Title</span>
                {getSortIcon('title')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('status')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Status</span>
                {getSortIcon('status')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('author')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Author</span>
                {getSortIcon('author')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('createdAt')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Created</span>
                {getSortIcon('createdAt')}
              </div>
            </th>
            <th 
              className="text-left p-4 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSort('updatedAt')}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Modified</span>
                {getSortIcon('updatedAt')}
              </div>
            </th>
            <th className="text-right p-4">
              <span className="text-sm font-medium text-foreground">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {posts?.map((post) => (
            <tr 
              key={post?.id} 
              className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
              onClick={() => onPreview(post?.id)}
            >
              <td className="p-4" onClick={(e) => e?.stopPropagation()}>
                <Checkbox
                  checked={selectedPosts?.includes(post?.id)}
                  onChange={(e) => onSelectPost(post?.id, e?.target?.checked)}
                />
              </td>
              <td className="p-4">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={post?.heroImage}
                    alt={post?.heroImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </td>
              <td className="p-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-foreground line-clamp-1">
                    {post?.title}
                  </span>
                  <span className="text-xs text-muted-foreground line-clamp-1">
                    {post?.excerpt}
                  </span>
                </div>
              </td>
              <td className="p-4">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                  post?.status === 'published' ?'bg-accent/10 text-accent' :'bg-muted text-muted-foreground'
                }`}>
                  <Icon 
                    name={post?.status === 'published' ? 'CheckCircle2' : 'Clock'} 
                    size={14} 
                    color="currentColor" 
                  />
                  {post?.status === 'published' ? 'Published' : 'Draft'}
                </div>
              </td>
              <td className="p-4">
                <span className="text-sm text-foreground">{post?.author}</span>
              </td>
              <td className="p-4">
                <span className="text-sm text-muted-foreground">{formatDate(post?.createdAt)}</span>
              </td>
              <td className="p-4">
                <span className="text-sm text-muted-foreground">{formatDate(post?.updatedAt)}</span>
              </td>
              <td className="p-4" onClick={(e) => e?.stopPropagation()}>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onPreview(post?.id)}
                    iconName="Eye"
                    iconSize={16}
                    title="Preview post"
                  >
                    <span className="sr-only">Preview post</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(post?.id)}
                    iconName="Edit"
                    iconSize={16}
                    title="Edit post"
                  >
                    <span className="sr-only">Edit post</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onToggleStatus(post?.id)}
                    iconName={post?.status === 'published' ? 'EyeOff' : 'Eye'}
                    iconSize={16}
                    title={post?.status === 'published' ? 'Unpublish' : 'Publish'}
                  >
                    <span className="sr-only">
                      {post?.status === 'published' ? 'Unpublish' : 'Publish'} post
                    </span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(post?.id)}
                    iconName="Trash2"
                    iconSize={16}
                    title="Delete post"
                  >
                    <span className="sr-only">Delete post</span>
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PostsTable;